import { useEffect, useMemo } from 'react';
import { LiveFeed } from './LiveFeed';
import { useTodaysMatchesQuery } from '@data/matches/useTodaysMatchesQuery';
import { useIsMobile } from '../../utils/useIsMobile';
import { ClubNameType } from '@ui/fixtures/contracts';
import FixtureListUI from '@ui/fixtures/components/FixtureList/FixtureListUI';
import { Link, LinkSize, LinkVariant } from '@ui/common/components/Link';
import { SocketProvider } from './SocketProvider';
import { Loader } from '@ui/common/components/Loader/Loader';

export function MatchCentrePageUI() {
  const fixturesResponse = useTodaysMatchesQuery({ withLiveScores: true });
  const isMobile = useIsMobile();

  const fixtures = fixturesResponse.data?.items ?? [];

  const competitionIds = useMemo(
    () =>
      fixturesResponse.data?.items
        ?.reduce((acc, fixture) => {
          if (!acc.includes(fixture.competition.id)) {
            acc.push(fixture.competition.id);
          }
          return acc;
        }, [] as string[])
        .sort(),
    [fixturesResponse.data?.items]
  );

  // Shows an alert when user tries to manually refresh page
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      // Show a confirmation dialog
      event.preventDefault();
      event.returnValue = ''; // Required for Chrome
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  return (
    <div className="match-centre">
      <div className="match-centre__title-container">
        <h1 className="match-centre__title">Match Centre</h1>
      </div>
      <div className="match-centre__content">
        {fixtures.length === 0 && fixturesResponse.isFetched && (
          <div className="match-centre__empty-state-container">
            <h3 className="match-centre__title">NO GAMES TODAY</h3>
            <Link
              url="/fixtures"
              label="Find Fixtures"
              variant={LinkVariant.Contained}
              size={LinkSize.m}
              openInNewTab={false}
            />
          </div>
        )}
        {!fixturesResponse.isFetched && (
          <div className="match-centre__loader-container">
            <Loader size={120} />
          </div>
        )}
        {fixturesResponse.isFetched && fixtures.length > 0 && (
          <>
            <div className="match-centre__component-container">
              <div className="match-centre__component-title-container">
                <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <h2 id="live-ticker">{isMobile ? 'LIVE TICKER' : 'Live ticker'}</h2>
                  <span className="match-centre__live-ticker-note">*auto-updates</span>
                </div>
                {isMobile && (
                  <Link
                    url="#todays-games"
                    label="TO TODAY'S GAMES"
                    variant={LinkVariant.Text}
                    size={LinkSize.m}
                    openInNewTab={false}
                  />
                )}
              </div>
              <LiveFeed />
            </div>
            <div className="match-centre__component-container">
              <div className="match-centre__component-title-container">
                <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <h2 id="todays-games">{isMobile ? `TODAY'S GAMES` : `Today's games`}</h2>
                  <span className="match-centre__live-ticker-note">*auto-updates</span>
                </div>

                {isMobile && (
                  <Link
                    url="#live-ticker"
                    label="to Live Ticker"
                    variant={LinkVariant.Text}
                    size={LinkSize.m}
                    openInNewTab={false}
                  />
                )}
              </div>
              <FixtureListUI
                className="matches-list__matches-container"
                fixtures={fixturesResponse.data?.items ?? []}
                columns={1}
                showAttendance={!isMobile}
                groupByDate
                groupByCompetition
                nameType={ClubNameType.ShortName}
                competitionsSort={competitionIds}
                isFixtureExpandable={!isMobile}
                sortByDate={'ASC'}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export function MatchCentrePage() {
  return (
    <SocketProvider>
      <MatchCentrePageUI />
    </SocketProvider>
  );
}
